/**
 * Keep the project document under Firestore's 1 MiB limit.
 *
 * The real size pressure is `pages[].canvasData`, not `project.video`
 * (~180 B per scene), so scene count is the cap that matters. Bytes are
 * measured AFTER encodeFirestoreValue because that is what Firestore stores.
 */

import {
  PROJECT_DOC_SOFT_LIMIT_BYTES,
  VIDEO_MAX_SCENE_MS,
  VIDEO_MAX_SCENES,
  VIDEO_MAX_TOTAL_MS,
  VIDEO_MIN_SCENE_MS,
  type Project,
  type VideoProject,
} from '@canvas/shared-types';
import { encodeFirestoreValue } from '@/lib/firestoreSafe';

/** Above this, adding scenes is blocked outright. */
export const PROJECT_DOC_HARD_LIMIT_BYTES = 950_000;

/** Approximate stored size of the project document in bytes. */
export function estimateProjectDocBytes(project: Project): number {
  try {
    const json = JSON.stringify(encodeFirestoreValue(project));
    return new TextEncoder().encode(json).length;
  } catch {
    return 0;
  }
}

export interface SceneBudgetResult {
  ok: boolean;
  /** User-facing explanation when `ok` is false. */
  reason?: string;
}

/**
 * Why a request to grow a scene was refused, or null when it fits.
 * Per-scene cap is 60 s; the whole timeline caps at 5 min.
 */
export function explainDurationClamp(
  video: VideoProject,
  pageId: string,
  requestedMs: number
): string | null {
  const scene = video.scenes.find((s) => s.pageId === pageId);
  if (!scene || requestedMs <= scene.durationMs + 20) return null;
  const others = video.totalDurationMs - scene.durationMs;
  const atTotalCap = scene.durationMs >= VIDEO_MAX_TOTAL_MS - others - 20;
  if (others + requestedMs > VIDEO_MAX_TOTAL_MS && atTotalCap) {
    return `Videos can be up to ${Math.round(VIDEO_MAX_TOTAL_MS / 60_000)} minutes long. Shorten another scene to make this one longer.`;
  }
  if (requestedMs > VIDEO_MAX_SCENE_MS && scene.durationMs >= VIDEO_MAX_SCENE_MS - 20) {
    return 'A scene can be up to 60 seconds. Split it into another scene to go longer.';
  }
  return null;
}

/** Can one more scene be added without breaking a limit? */
export function assertSceneBudget(
  video: VideoProject,
  docBytes: number
): SceneBudgetResult {
  if (video.scenes.length >= VIDEO_MAX_SCENES) {
    return {
      ok: false,
      reason: `Videos can have up to ${VIDEO_MAX_SCENES} scenes. Remove a scene to add another.`,
    };
  }
  if (video.totalDurationMs + VIDEO_MIN_SCENE_MS > VIDEO_MAX_TOTAL_MS) {
    return {
      ok: false,
      reason: `Videos can be up to ${Math.round(VIDEO_MAX_TOTAL_MS / 60_000)} minutes long. Shorten a scene to add another.`,
    };
  }
  if (docBytes >= PROJECT_DOC_HARD_LIMIT_BYTES) {
    return {
      ok: false,
      reason: 'This project is close to the storage limit. Simplify or remove a scene before adding more.',
    };
  }
  return { ok: true };
}

export type BudgetLevel = 'ok' | 'warn' | 'block';

export interface BudgetDescription {
  level: BudgetLevel;
  /** e.g. "612 KB of 1 MB" */
  label: string;
}

/** Classify a document size for the timeline's storage banner. */
export function describeBudget(docBytes: number): BudgetDescription {
  const kb = Math.round(docBytes / 1000);
  const label = `${kb} KB of 1 MB`;
  if (docBytes >= PROJECT_DOC_HARD_LIMIT_BYTES) return { level: 'block', label };
  if (docBytes >= PROJECT_DOC_SOFT_LIMIT_BYTES) return { level: 'warn', label };
  return { level: 'ok', label };
}
